import { ExternalLink, IndianRupeeIcon, Package, ShoppingCart, Store, User, TrendingUp } from "lucide-react";
import { useState } from "react";
import useStats from "../../hooks/useStats";
import ConfirmDialog from "../ConfirmDialog";
import OrderList from "../order/OrderList";
import { StatsCard } from "./StatsCard";
import useEntity from "../../hooks/useEntity";
import { useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const [showStoreStatusDialog, setShowStoreStatusDialog] = useState(false);

  const { data, error, refetch } = useEntity();

  const { data: stats, isLoading } = useStats();

  const navigate = useNavigate();

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return "Good morning";
    if (currentHour < 18) return "Good afternoon";
    if (currentHour < 21) return "Good evening";
    return "Good night";
  };

  const handleBackNavigation = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/admin/orders');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-white to-indigo-50/50 rounded-xl p-4 sm:p-6 md:p-8 shadow-sm border border-indigo-100/50 mb-4 sm:mb-6 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-900 to-blue-600 bg-clip-text text-transparent">
              {getGreeting()}
            </h1>
            <a
              href={`https://${(data as any)?.storeUrl}.mart-24.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-600 rounded-lg sm:rounded-xl hover:from-indigo-100 hover:to-blue-100 transition-all duration-300 group text-sm sm:text-base border border-indigo-100/50 shadow-sm hover:shadow-md"
            >
              <Store className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-center">Visit Store</span>
              <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 transform group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-sm border border-indigo-100/50 animate-pulse">
                <div className="h-3 sm:h-4 bg-indigo-100/50 rounded w-3/4 mb-3 sm:mb-4"></div>
                <div className="h-6 sm:h-8 bg-indigo-100/50 rounded w-1/2 mb-3 sm:mb-4"></div>
                <div className="h-3 sm:h-4 bg-indigo-100/50 rounded w-1/4"></div>
              </div>
            ))
          ) : (
            <>
              <StatsCard
                title="Total Sales"
                value={stats?.totalSales!}
                trend={0}
                icon={IndianRupeeIcon}
                color="bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-600 border-emerald-100/50"
                isCurrency={true}
              />
              <StatsCard
                title="Total Orders"
                value={stats?.totalOrders!}
                trend={0}
                icon={ShoppingCart}
                color="bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 border-blue-100/50"
              />
              <StatsCard
                title="Total Products"
                value={stats?.totalProducts!}
                trend={0}
                icon={Package}
                color="bg-gradient-to-br from-violet-50 to-purple-50 text-violet-600 border-violet-100/50"
              />
              <StatsCard
                title="Total Customers"
                value={stats?.totalCustomers!}
                trend={0}
                icon={User}
                color="bg-gradient-to-br from-amber-50 to-orange-50 text-amber-600 border-amber-100/50"
              />
            </>
          )}
        </div>

        {/* Recent Orders Section */}
        <div className="bg-gradient-to-br from-white to-indigo-50/30 rounded-xl sm:rounded-2xl shadow-sm border border-indigo-100/50 overflow-hidden backdrop-blur-sm">
          <div className="p-4 sm:p-6 border-b border-indigo-100/50 bg-gradient-to-r from-white to-indigo-50/50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-indigo-900 to-blue-600 bg-clip-text text-transparent">Recent Orders</h2>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-indigo-600 bg-indigo-50/50 px-2.5 py-1 rounded-full border border-indigo-100/50">
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Last 7 days</span>
              </div>
            </div>
          </div>
          <OrderList status="recent" />
        </div>

        {/* Store Status Dialog */}
        <ConfirmDialog
          isOpen={showStoreStatusDialog}
          onCancel={() => setShowStoreStatusDialog(false)}
          onConfirm={() => { }}
          title="Store Status"
          message="Store status functionality has been removed."
          confirmLabel="OK"
        />
      </main>
    </div>
  );
};

export default AdminDashboard;
